<script>
export default {
  name: 'ShowroomModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    inventory: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      loading: false,
      iframeSrc: false,
      baseURL: process.env.VDP_BASEURL
    }
  },
  watch: {
    show: {
      handler: function (value) {
        this.loading = !!this.inventory
        if (value) {
          this.$nextTick(() => {
            this.$refs['max-showroom'].focus()
          })
        }
      },
      immediate: true
    }
  },
  mounted () {
    document.body.insertBefore(this.$el, document.body.lastChild)
  },
  created () {
    this.iframeSrc = this.inventory && `${this.baseURL}/vehicle/${this.inventory}/embed`
  },
  methods: {
    close () {
      this.$emit('close')
    }
  }
}
</script>

<template>
  <transition
    :enter-active-class="$style.wrapperTransition"
    :leave-active-class="$style.wrapperTransition"
  >
    <div
      v-if="show"
      id="max-showroom"
      ref="max-showroom"
      tabindex="0"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      :class="[$style.wrapper, $style.ghentcadillac]"
      @keydown.esc="close"
    >
      <header :class="$style.header">
        <h2
          id="modal-title"
          hidden
        >
          ACV MAX Showroom
        </h2>
        <button
          aria-labelledby="close-label"
          :class="$style.closeButton"
          @click.stop="close"
        >
          <span
            id="close-label"
            hidden
          >Close</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            role="img"
            aria-hidden="true"
          ><path
            fill="white"
            d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
          /></svg>
        </button>
      </header>
      <div
        v-if="loading"
        :class="$style.overlay"
      >
        <span
          :class="$style.spinner"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <span /><span /><span /><span />
        </span>
      </div>
      <iframe
        v-if="iframeSrc"
        class="chromatic-ignore"
        :class="$style.iframe"
        :src="iframeSrc"
        title="VDP"
        @load="loading = false"
      />
      <p
        v-else
        :class="$style.alert"
      >
        Configuration error:
        <a
          href="https://embed.maxapps.io/docs/#/plugins/M4W?id=data-inventory"
          target="_blank"
        >Please provide the VIN of the vehicle to display.</a>
      </p>
    </div>
  </transition>
</template>

<style lang="scss" module>
$header-height: 3.5rem;
$spacing: 1.25rem;
$z-index-max-value: 2147483647;

.wrapper,
.header,
.closeButton,
.iframe,
.overlay,
.spinner {
  all: unset;
}

.wrapper[role="dialog"] {
  background-color: #FAFAFA;
  height: 100%;
  left: 0;
  opacity: 1;
  overflow: hidden;
  position: fixed;
  top: 0;
  transform: translateY(0);
  width: 100%;
  z-index: $z-index-max-value;
}

// Ghentcadillac.com specifics styles
.ghentcadillac[role="dialog"] {
  display: block;
  max-height: 100% !important;

  // Force replace transitions
  transition-duration: 0.2s !important;
  transition-property: opacity, transform !important;
  transition-timing-function: ease-in-out !important;
}

.wrapperTransition[role="dialog"] {
  opacity: 0;
  transform: translateY(10%);
}

.header {
  align-items: center;
  background-color: #315084;
  box-shadow:
    0 2px 4px -1px rgb(0 0 0 / 20%),
    0 4px 5px 0 rgb(0 0 0 / 14%),
    0 1px 10px 0 rgb(0 0 0 / 12%);
  display: flex;
  height: 60px;
  justify-content: flex-end;
  margin-bottom: $spacing;
  padding: 0 $spacing;

  h2 {
    display: none;
  }
}

.closeButton {
  align-items: center;
  background-color: transparent;
  border: none;
  border-radius: 100%;
  cursor: pointer;
  display: flex;
  height: 3rem;
  justify-content: center;
  text-align: center;
  transition: background-color 0.3s ease-in-out;
  width: 3rem;

  span {
    display: none;
  }

  &:hover {
    background-color: #405D8D;
  }

  &:active,
  &:focus {
    background-color: #8195B2;
  }

  svg {
    display: inline-block;
    height: 1.5rem;
    width: 1.5rem;
  }
}

.iframe {
  border: 0;
  height: calc(100% - #{$spacing} - #{$header-height});
  margin: 0;
  width: 100%;
}

.alert {
  font-family: system-ui;
  padding: 4rem;
  text-align: center;

  a { color: #405D8D; }
}

.overlay {
  align-items: center;
  background-color: #212121;
  bottom: 0;
  display: flex;
  height: calc(100% - #{$header-height});
  justify-content: center;
  left: 0;
  opacity: 0.46;
  position: absolute;
  width: 100%;
}

.spinner {
  display: inline-block;
  height: 80px;
  position: relative;
  width: 80px;

  span {
    animation: spinner 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border: 8px solid #FFF;
    border-color: #FFF transparent transparent;
    border-radius: 50%;
    box-sizing: border-box;
    display: block;
    height: 64px;
    margin: 8px;
    position: absolute;
    width: 64px;
  }

  span:nth-child(1) {
    animation-delay: -0.45s;
  }

  span:nth-child(2) {
    animation-delay: -0.3s;
  }

  span:nth-child(3) {
    animation-delay: -0.15s;
  }

  @keyframes spinner {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }
}
</style>
